/**
 * ============================================================================
 * REPORTING API CLIENT — shared transport base for all middleware accessors.
 * ============================================================================
 *
 * LAYER: accessor (portal convention). The ONLY code in the app that performs HTTP to the
 * middleware. Pattern precedent: ADSProjectPortalUI MetricCalloutsApi /
 * ADSCustomerHubUICommonComponents accessors.
 *
 * OWNS (and nothing else owns):
 * - Stage base-URL selection: local dev goes same-origin `/api` through the
 *   webpack dev-server proxy (configuration/devApiProxy.js — API GW doesn't
 *   answer CORS preflight yet); deployed goes direct to the stage endpoint.
 * - AUTH: Midway session. Deployed cross-origin calls go through SentryFetch
 *   (MidwayClientSuite / MCS) so authentication survives MCS browser
 *   enforcement; the Midway cookie still rides via `credentials: include`.
 * - Error taxonomy: HTTP status → ErrorCode, thrown as ReportingApiError.
 * - Body handling: text-first parse — API Gateway 5xx pages are HTML, and
 *   response.json() would consume the stream AND lose the real status.
 *
 * MUST NOT contain: caching decisions, aggregation, business rules, or any
 * knowledge of which component wants the data.
 */

import { configure } from '@amzn/sentry-fetch';

import { getApiConfig } from 'src/accessors/apiConfig';

/**
 * One reusable MidwayClientSuite (MCS)-aware fetch instance for the deployed,
 * cross-origin, Midway-protected API Gateway. SentryFetch is an opt-in `fetch`
 * wrapper, so we reuse a single configured instance (per the library's
 * performance guidance) rather than wrapping per request. No enable flag is
 * needed: MCS is auto-detected for commercial Midway endpoints. This keeps the
 * Midway session primed under MCS browser enforcement, which removes the legacy
 * unsigned cookie-priming fallback the browser previously relied on.
 */
const mcsFetch = configure();

export enum ErrorCode {
  AuthFailure = 'AuthFailure',
  ValidationFailure = 'ValidationFailure',
  Conflict = 'Conflict',
  InternalFailure = 'InternalFailure',
  NetworkFailure = 'NetworkFailure',
}

export class ReportingApiError extends Error {
  // NOTE: no TS constructor parameter properties here — the katal webpack
  // Babel transform doesn't support them (build error: "'public' is reserved").
  public readonly status: number;
  public readonly errorCode: ErrorCode;
  public readonly body?: unknown;

  constructor(status: number, errorCode: ErrorCode, body?: unknown) {
    super(`${errorCode} (HTTP ${status})`);
    this.name = 'ReportingApiError';
    this.status = status;
    this.errorCode = errorCode;
    this.body = body;
  }
}

function errorCodeForStatus(status: number): ErrorCode {
  switch (status) {
    case 400:
      return ErrorCode.ValidationFailure;
    case 401:
    case 403:
      return ErrorCode.AuthFailure;
    case 409:
      return ErrorCode.Conflict;
    default:
      return ErrorCode.InternalFailure;
  }
}

function isLocalDevServer(): boolean {
  const { hostname } = window.location;
  return hostname === 'localhost' || hostname === '0.0.0.0' || hostname.startsWith('local.');
}

export abstract class ReportingApiClient {
  private getBaseUrl(): string {
    return isLocalDevServer() ? '/api' : getApiConfig().endpoint;
  }

  /** POST JSON to the middleware; typed result or ReportingApiError. */
  protected async post<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'POST',
      body: JSON.stringify(body ?? {}),
      headers: { 'Content-Type': 'application/json' },
    });
  }

  /** GET from the middleware; typed result or ReportingApiError. */
  protected async get<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: 'GET' });
  }

  private async request<T>(path: string, init: RequestInit): Promise<T> {
    const url = `${this.getBaseUrl()}${path}`;
    const requestInit: RequestInit = {
      ...init,
      credentials: 'include', // Midway cookie on cross-origin (deployed stages)
    };
    let response: Response;
    try {
      // Deployed stages call the cross-origin, Midway-protected API Gateway, so
      // they go through SentryFetch (MCS-backed session priming; auto-detected
      // for commercial Midway). Local dev talks same-origin to the webpack
      // dev-server proxy (configuration/devApiProxy.js) — not a Midway endpoint
      // — so it uses the native transport and skips the MCS auth probe.
      response = isLocalDevServer()
        ? await fetch(url, requestInit)
        : await mcsFetch(url, requestInit);
    } catch (error: any) {
      throw new ReportingApiError(0, ErrorCode.NetworkFailure, error?.message ?? 'Network error');
    }

    const text = await response.text();
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }

    if (!response.ok) {
      throw new ReportingApiError(response.status, errorCodeForStatus(response.status), parsed);
    }
    return parsed as T;
  }
}
